import React from 'react';
import { FaChevronLeft, FaChevronRight, FaStar } from 'react-icons/fa';
import { Star } from '../icons';
import Loader from '../common/Loader';
import { useLanguage } from '../../contexts/LanguageContext';

const ReviewsSection = React.memo(({
    latestReviews,
    totalReviews,
    ratingStats,
    reviewsPage,
    reviewsLoading,
    onNextPage,
    onPrevPage,
    isLastPage,
    reviewsPerPage,
    maskName,
}) => {
    const { t } = useLanguage();
    return (
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-10">
                    <span className="inline-block px-3 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-semibold mb-3 border border-amber-100">{t('reviews.badge')}</span>
                    <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {t('reviews.title')}
                    </h2>
                    <p className="text-gray-500 text-sm">{t('reviews.subtitle').replace('{count}', totalReviews)}</p>
                </div>

                {reviewsLoading ? (
                    <div className="flex justify-center py-12"><Loader /></div>
                ) : (
                    <>
                        {/* Rating Summary */}
                        <div className="flex flex-col md:flex-row gap-8 mb-8">
                            {/* Left: Overall Rating */}
                            <div className="md:w-72 flex-shrink-0">
                                <div className="rounded-2xl p-6 text-center border border-gray-100" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFBEB 100%)' }}>
                                    <div className="text-5xl font-black text-gray-900 mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{ratingStats.avg || '0'}</div>
                                    <div className="flex items-center justify-center gap-1 mb-2">
                                        {[1, 2, 3, 4, 5].map(s => (
                                            <FaStar key={s} className={`text-lg ${s <= Math.round(ratingStats.avg) ? 'text-amber-400' : 'text-gray-200'}`} />
                                        ))}
                                    </div>
                                    <p className="text-gray-500 text-sm mb-4">{t('reviews.fromReviews').replace('{count}', totalReviews)}</p>
                                    <div className="space-y-2 text-left">
                                        {[5, 4, 3, 2, 1].map((star, i) => ({ stars: star, pct: ratingStats.dist[i] || 0 })).map(r => (
                                            <div key={r.stars} className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500 w-3 text-right">{r.stars}</span>
                                                <FaStar className="text-amber-400 text-[10px]" />
                                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${r.pct}%` }} />
                                                </div>
                                                <span className="text-[10px] text-gray-400 w-8">{r.pct}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Review Cards */}
                            <div className="flex-1 space-y-3">
                                {latestReviews.map(review => (
                                    <div key={review.id} className="rounded-xl p-4 border border-gray-100 hover:border-blue-200 transition-all hover:shadow-md bg-white group">
                                        <div className="flex items-start gap-3">
                                            {review.photoURL ? (
                                                <img
                                                    src={review.photoURL}
                                                    alt=""
                                                    width="40"
                                                    height="40"
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-10 h-10 rounded-full flex-shrink-0 object-cover border-2 border-gray-100"
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                />
                                            ) : null}
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${review.photoURL ? 'hidden' : ''}`} style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)', display: review.photoURL ? 'none' : 'flex' }}>
                                                <span className="text-white font-bold text-sm">{(review.displayName || review.userEmail || '?').charAt(0).toUpperCase()}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-900 font-semibold text-sm">{maskName(review.displayName || review.userEmail?.split('@')[0])}</span>
                                                        {review.gameName && <span className="text-xs bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full">{review.gameName}</span>}
                                                    </div>
                                                    <div className="flex items-center gap-0.5">
                                                        {[1, 2, 3, 4, 5].map(star => (
                                                            <Star key={star} className={`w-3 h-3 ${star <= (review.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                                                        ))}
                                                    </div>
                                                </div>
                                                {(review.comment || review.text) && (
                                                    <p className="text-gray-600 text-sm leading-relaxed mt-1">{review.comment || review.text}</p>
                                                )}
                                                {review.createdAt && (
                                                    <p className="text-gray-400 text-xs mt-1.5">
                                                        {(review.createdAt.toDate ? review.createdAt.toDate() : new Date(review.createdAt)).toLocaleDateString(t('reviews.dateLocale'), { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {totalReviews > reviewsPerPage && (
                            <div className="flex items-center justify-center gap-4 mt-6">
                                <button onClick={onPrevPage} disabled={reviewsPage === 1} className="p-2.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-blue-500 hover:border-blue-300 disabled:opacity-30 transition-all disabled:cursor-not-allowed">
                                    <FaChevronLeft className="text-sm" />
                                </button>
                                <span className="text-sm text-gray-500 font-medium">{reviewsPage} / {Math.ceil(totalReviews / reviewsPerPage)}</span>
                                <button onClick={onNextPage} disabled={isLastPage} className="p-2.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-blue-500 hover:border-blue-300 disabled:opacity-30 transition-all disabled:cursor-not-allowed">
                                    <FaChevronRight className="text-sm" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section >
    );
});

ReviewsSection.displayName = 'ReviewsSection';

export default ReviewsSection;
